/**
 * Custom ShopStocked chat launcher for Shopify Inbox.
 *
 * DOM notes (verified in browser):
 * - Opening replaces light-DOM activator with <shopify-agent-runtime>
 * - host.show() opens reliably
 * - host.close() clears the `open` attribute but DOES NOT hide the panel
 * - Clicking button[aria-label="Close chat"] inside nested shadow sets display:none
 */
(function () {
  const LAUNCHER_ID = 'ss-inbox-launcher';
  const STYLE_ATTR = 'data-ss-chat';

  const BOLD_ICON = `
    <svg class="ss-inbox-launcher__icon" xmlns="http://www.w3.org/2000/svg" viewBox="5 1 38 36" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        stroke-width="2.75"
        stroke-linejoin="round"
        stroke-linecap="round"
        d="M15.5 8h17c3.6 0 6.5 2.9 6.5 6.5v8.5c0 3.6-2.9 6.5-6.5 6.5H26.2l-6.2 6.5c-.8.85-2.2.25-2.2-.95V29.5H15.5c-3.6 0-6.5-2.9-6.5-6.5v-8.5C9 10.9 11.9 8 15.5 8z"
      />
    </svg>
  `;

  const CLOSE_ICON = `
    <svg class="ss-inbox-launcher__icon ss-inbox-launcher__icon--close" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
        d="M15 15l18 18M33 15L15 33"
      />
    </svg>
  `;

  function getChatHost() {
    return document.querySelector('shopify-chat');
  }

  function getRuntime(host) {
    return host?.shadowRoot?.querySelector('shopify-agent-runtime') || null;
  }

  function getDialogPanel(host) {
    const runtime = getRuntime(host);
    return runtime?.shadowRoot?.querySelector('section.panel, [part="dialog"]') || null;
  }

  function isPanelVisible(host) {
    const panel = getDialogPanel(host);
    if (!panel) return false;
    const style = window.getComputedStyle(panel);
    /* display:none is the real closed signal from Inbox’s Close button */
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    const rect = panel.getBoundingClientRect();
    return rect.width > 1 && rect.height > 1;
  }

  function isChatOpen(host) {
    if (!host) return false;
    /* Panel visibility wins — host.close() can clear `open` while panel stays up */
    if (isPanelVisible(host)) return true;
    if (host.hasAttribute('open')) return true;
    if (getRuntime(host)?.hasAttribute('open')) return true;
    return false;
  }

  function findInShadows(root, selector, depth) {
    if (!root || depth > 6) return null;
    const scope = root.shadowRoot || root;
    if (scope.querySelector) {
      const hit = scope.querySelector(selector);
      if (hit) return hit;
      const nodes = scope.querySelectorAll ? scope.querySelectorAll('*') : [];
      for (let i = 0; i < nodes.length; i += 1) {
        if (nodes[i].shadowRoot) {
          const nested = findInShadows(nodes[i], selector, depth + 1);
          if (nested) return nested;
        }
      }
    }
    return null;
  }

  function findCloseButton(host) {
    return (
      findInShadows(host, 'button[aria-label="Close chat"]', 0) ||
      findInShadows(host, 'button[aria-label*="Close" i]', 0)
    );
  }

  function findActivator(host) {
    return (
      host?.shadowRoot?.querySelector('button.activator') ||
      findInShadows(host, 'button.activator', 0)
    );
  }

  function openChat(host) {
    if (!host) return false;
    if (typeof host.show === 'function') {
      host.show();
      return true;
    }
    const activator = findActivator(host);
    if (activator) {
      activator.click();
      return true;
    }
    return false;
  }

  function closeChat(host) {
    if (!host) return false;

    /* Prefer the real Close control — host.close() does not hide the panel */
    const closeBtn = findCloseButton(host);
    if (closeBtn) {
      closeBtn.click();
      return true;
    }

    if (typeof host.close === 'function') {
      host.close();
    }

    const panel = getDialogPanel(host);
    if (panel && isPanelVisible(host)) {
      panel.style.setProperty('display', 'none', 'important');
    }
    host.removeAttribute('open');
    getRuntime(host)?.removeAttribute('open');
    return true;
  }

  function toggleChat(host) {
    if (!host) return;
    if (isChatOpen(host)) closeChat(host);
    else openChat(host);
  }

  function styleNativeActivator(host) {
    if (!host?.shadowRoot) return;

    const css = `
      button.activator {
        opacity: 0 !important;
        pointer-events: none !important;
        width: 1px !important;
        height: 1px !important;
        min-width: 0 !important;
        min-height: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
        position: absolute !important;
        bottom: 0 !important;
        right: 0 !important;
      }

      /* Lift chat panel above our 64px launcher + gap */
      section.panel,
      [part="dialog"] {
        bottom: 100px !important;
      }

      @media screen and (max-width: 749px) {
        section.panel,
        [part="dialog"] {
          bottom: 92px !important;
        }
      }
    `;

    let style = host.shadowRoot.querySelector(`style[${STYLE_ATTR}]`);
    if (!style) {
      style = document.createElement('style');
      style.setAttribute(STYLE_ATTR, '1');
      host.shadowRoot.appendChild(style);
    }
    if (style.textContent !== css) style.textContent = css;
  }

  function syncLauncherState(launcher, host) {
    if (!launcher) return;
    launcher.hidden = false;
    launcher.removeAttribute('hidden');
    const open = isChatOpen(host);
    launcher.setAttribute('aria-expanded', open ? 'true' : 'false');
    launcher.setAttribute('aria-label', open ? 'Close chat' : 'Open chat');
    launcher.classList.toggle('ss-inbox-launcher--open', open);

    const showingClose = !!launcher.querySelector('.ss-inbox-launcher__icon--close');
    if (open && !showingClose) {
      launcher.innerHTML = CLOSE_ICON;
    } else if (!open && showingClose) {
      launcher.innerHTML = BOLD_ICON;
    }
  }

  function bindLauncher(launcher) {
    if (launcher.dataset.amToggleBound) return;
    launcher.dataset.amToggleBound = '1';

    launcher.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const host = getChatHost();
      if (!host) return;

      toggleChat(host);
      window.setTimeout(() => syncLauncherState(launcher, host), 50);
      window.setTimeout(() => syncLauncherState(launcher, host), 350);
    });
  }

  function ensureLauncher() {
    let launcher = document.getElementById(LAUNCHER_ID);
    if (launcher) {
      if (!launcher.querySelector('svg.ss-inbox-launcher__icon')) {
        launcher.innerHTML = BOLD_ICON;
      }
      bindLauncher(launcher);
      return launcher;
    }

    launcher = document.createElement('button');
    launcher.id = LAUNCHER_ID;
    launcher.type = 'button';
    launcher.className = 'ss-inbox-launcher';
    launcher.setAttribute('aria-label', 'Open chat');
    launcher.setAttribute('aria-expanded', 'false');
    launcher.innerHTML = BOLD_ICON;
    document.body.appendChild(launcher);
    bindLauncher(launcher);
    return launcher;
  }

  function enhance() {
    const host = getChatHost();
    if (!host?.shadowRoot) return false;

    styleNativeActivator(host);
    const launcher = ensureLauncher();
    syncLauncherState(launcher, host);

    if (!host.dataset.amChatObserved) {
      host.dataset.amChatObserved = '1';

      const sync = () => {
        styleNativeActivator(host);
        syncLauncherState(document.getElementById(LAUNCHER_ID), getChatHost());
      };

      const observer = new MutationObserver(sync);
      observer.observe(host, {
        attributes: true,
        attributeFilter: ['open', 'is-open', 'class', 'style'],
      });

      window.setInterval(sync, 1000);
    }

    return true;
  }

  function start() {
    ensureLauncher();
    if (enhance()) return;

    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (enhance() || tries > 40) window.clearInterval(timer);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
